import "./GroupSelector.scss";

import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	useRef,
} from "react";
import clsx from "clsx";
import throttle from "lodash.throttle";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";

import * as GroupUtils from "../../../lib/util/GroupUtils";
import * as AuthUtils from "../../../lib/util/AuthUtils";
import * as Utils from "../../../lib/util/Utils";
import * as FormatHooks from "../../../lib/hook/FormatHooks";
import * as strings from "../../../strings";

import Text from "../../common/Text/Text";
import Button from "../../common/Button/Button";
import MenuTrigger from "../../nav/MenuTrigger/MenuTrigger";
import Modal from "../../container/Modal/Modal";
import Checkbox from "../../form/Checkbox/Checkbox";
import SearchFilter from "../../common/SearchFilter/SearchFilter";

import { TrackingContext } from "../../../lib/util/AnalyticsUtils";
import { Group, FlattenedGroup } from "../../../lib/types/Group";
import { useSelector } from "../../../lib/hook/ReduxHooks";
import { useTranslated } from "../../../lib/hook/LocaleHooks";
import { Translatable } from "lib/util/Translatable";
import { selectCurrentScopeGroupsAvailableToUser } from "../../../store/Scope/ScopeSelectors";

interface GroupSelectorProps {
	selectedGroup: string;
	onSelect: (id?: string) => void;
	invert?: boolean;
	disabled?: boolean;
	multiSelect?: boolean;
	open?: boolean;
	noTrigger?: boolean;
	confirmSelect?: boolean;
	confirmContent?: (groupId: string) => React.ReactNode;
	readOnly?: boolean;
	noGroupText?: string | Translatable;
	disableRootSelect?: boolean;
	modalClassName?: string;
	headerContent?: React.ReactNode;
}

const GroupSelector: React.FC<GroupSelectorProps> = (props) => {
	const { groups } = useSelector((state) => ({
		groups: selectCurrentScopeGroupsAvailableToUser(state).value,
	}));

	const {
		disabled,
		selectedGroup,
		invert,
		open,
		noTrigger,
		onSelect,
		multiSelect,
		readOnly,
		noGroupText,
		disableRootSelect,
		confirmSelect,
		confirmContent,
		modalClassName,
		headerContent,
	} = props;

	const modalContentRef: React.MutableRefObject<HTMLDivElement | null> =
		useRef(null);
	const selectedGroupRef = useRef<HTMLDivElement>(null);
	const fleetName = FormatHooks.useFormattedScopeFleetName();
	const formatGroupIdName = FormatHooks.useFormatGroupIdName();
	const selectedGroupName = useMemo(
		() => (selectedGroup ? formatGroupIdName(selectedGroup) : fleetName),
		[formatGroupIdName, selectedGroup, fleetName],
	);
	const [translatedFleetName] = useTranslated(fleetName);
	const includeRoot = useMemo(
		() => !disableRootSelect && AuthUtils.canAccessRoot(),
		[disableRootSelect],
	);

	const trackingContext = useContext(TrackingContext);
	const [modalActive, setModalActive] = useState(false);
	const [scrollToGroupActive, setScrollToGroupActive] = useState(false);
	const [scrollToTopActive, setScrollToTopActive] = useState(false);
	const [filteredGroups, setFilteredGroups] = useState<Array<
		FlattenedGroup | { name: string }
	> | null>(null);
	const [currentGroupSelection, setCurrentGroupSelection] = useState<
		string | null
	>(null);
	const [confirmScreenActive, setConfirmScreenActive] = useState(false);

	const onScroll = useCallback(() => {
		if (!modalContentRef.current) {
			return;
		}

		const showScrollToTop = modalContentRef.current.scrollTop > 200;

		if (scrollToTopActive !== showScrollToTop) {
			setScrollToTopActive(showScrollToTop);
		}
	}, [scrollToTopActive]);

	const onModalRefSet = useCallback(
		(current: HTMLDivElement | null) => {
			modalContentRef.current = current;
			current?.addEventListener("scroll", throttle(onScroll, 100));
		},
		[onScroll],
	);

	const rootDepth = groups?.[0]?.depth || 0;
	const flatGroups = useMemo(() => {
		const allGroups: Array<any | { name: string }> = GroupUtils.flattenGroups(
			groups,
		).map((group) => ({
			...group,
			name: formatGroupIdName(group.id),
			depth: (includeRoot ? group.depth + 1 : group.depth) - rootDepth,
		}));

		if (includeRoot) {
			allGroups.unshift({ name: translatedFleetName });
		}

		return allGroups;
	}, [groups, includeRoot, formatGroupIdName, rootDepth, translatedFleetName]);

	const groupsToShow = useMemo(
		() => (filteredGroups !== null ? filteredGroups : flatGroups),
		[filteredGroups, flatGroups],
	);

	const handleFilter = useCallback(
		(validGroups) => {
			setFilteredGroups(
				flatGroups?.filter((group: FlattenedGroup | { name: string }) =>
					validGroups.includes(group.name),
				),
			);

			const isScrollable = Utils.isElementScrollable(modalContentRef.current);

			if (
				(!isScrollable && !scrollToGroupActive) ||
				(!validGroups.includes(selectedGroupName) && !scrollToGroupActive) ||
				(validGroups.includes(selectedGroupName) &&
					scrollToGroupActive === isScrollable)
			) {
				// scrollToGroupActive is already the correct value
				return;
			}

			if (!isScrollable || !validGroups.includes(selectedGroupName)) {
				setScrollToGroupActive(false);
			} else if (validGroups.includes(selectedGroupName)) {
				setScrollToGroupActive(isScrollable);
			}
		},
		[flatGroups, scrollToGroupActive, selectedGroupName],
	);

	const onVisibleChange = useCallback((visible) => {
		setModalActive(visible);

		if (!visible) {
			window.setTimeout(() => {
				setFilteredGroups(null);
				setCurrentGroupSelection(null);
				setConfirmScreenActive(false);
			}, 200);
		}
	}, []);

	const handleTriggerClick = useCallback(() => {
		if (disabled || readOnly) {
			return;
		}

		onVisibleChange(!modalActive);
	}, [modalActive, onVisibleChange, disabled, readOnly]);

	const handleSelect = useCallback(
		(id: string, checked?: boolean) => {
			if (checked) {
				onSelect(id);
			}

			if (!multiSelect) {
				onVisibleChange(false);
			}
		},
		[onSelect, multiSelect, onVisibleChange],
	);

	const handleGroupChange = useCallback(
		(evt, checked, id) => {
			if (checked) {
				if (confirmSelect) {
					setCurrentGroupSelection(id);
					return setConfirmScreenActive(true);
				}
			}

			handleSelect(id, checked);
		},
		[confirmSelect, handleSelect],
	);

	const handleConfirm = useCallback(() => {
		onVisibleChange(false);
		handleSelect(currentGroupSelection || "", true);
	}, [onVisibleChange, handleSelect, currentGroupSelection]);

	const handleConfirmCancel = useCallback(() => {
		onVisibleChange(false);
	}, [onVisibleChange]);

	useEffect(() => {
		if (open === undefined) {
			return;
		}

		setModalActive(open);
	}, [open]);

	const handleModalOpened = useCallback(() => {
		const isScrollable =
			Utils.isElementScrollable(modalContentRef.current) &&
			(selectedGroupRef.current?.offsetTop || 0) >
				(modalContentRef.current?.clientHeight || 0);

		if (scrollToTopActive) {
			setScrollToTopActive(false);
		}

		if (isScrollable !== scrollToGroupActive) {
			setScrollToGroupActive(isScrollable);
		}
	}, [scrollToGroupActive, scrollToTopActive]);

	const scrollToPosition = useCallback((pos) => {
		modalContentRef.current?.scrollTo({
			left: pos.x,
			top: pos.y,
			behavior: "smooth",
		});
	}, []);

	const scrollToSelected = useCallback(() => {
		if (!selectedGroupRef.current) {
			return;
		}

		scrollToPosition({ x: 0, y: selectedGroupRef.current.offsetTop - 50 });
	}, [scrollToPosition]);

	const scrollToTop = useCallback(() => {
		scrollToPosition({ x: 0, y: 0 });
	}, [scrollToPosition]);

	return (
		<TrackingContext.Provider
			value={{
				ga: `Group Selector - ${trackingContext}`,
				mixpanel: {
					name: "Group Selector",
					Context: trackingContext,
				},
			}}
		>
			{!noTrigger && (
				<MenuTrigger
					className={clsx("GroupSelector__trigger", {
						"--invert": invert,
						"--disabled": disabled || readOnly,
					})}
					showOverflowOnHover={true}
					onClick={handleTriggerClick}
				>
					<Text>
						{noGroupText && !selectedGroup ? noGroupText : selectedGroupName}
					</Text>
				</MenuTrigger>
			)}
			<Modal
				className={clsx("GroupSelector__modal --pdf-ignore", modalClassName)}
				visible={modalActive && !disabled}
				onVisibleChange={onVisibleChange}
				dynamicHeight={flatGroups.length <= 15 || confirmScreenActive}
				onFinishedOpen={handleModalOpened}
				forwardRef={onModalRefSet}
			>
				<div className={"GroupSelector__modal-content"}>
					{!confirmScreenActive && (
						<>
							<Text tag={"p"} className={"GroupSelector__modal-header"}>
								{headerContent ? (
									headerContent
								) : (
									<Text>
										{strings.GROUP_SELECTOR_CURRENTLY_VIEWING.with({
											groupName: selectedGroupName,
										})}
									</Text>
								)}
								{scrollToGroupActive && (
									<Button
										onClick={scrollToSelected}
										className={"GroupSelector__modal-scroll-btn"}
									>
										<FaArrowDown />
									</Button>
								)}
							</Text>
							{flatGroups.length > 15 && (
								<SearchFilter
									items={flatGroups}
									onFilter={handleFilter}
									primaryKey={"name"}
									searchProps={["name"]}
									placeholder={strings.ADVANCED_SEARCH_GROUPS_PLACEHOLDER}
								/>
							)}
							<div className={"GroupSelector__group-list"}>
								{groupsToShow?.map(
									(
										group:
											| Group
											| {
													name: string;
													id?: string;
													depth?: number;
											  },
										index: number,
									) => {
										const checked = selectedGroup
											? selectedGroup === group?.id
											: !group.id;

										return (
											<div
												className={"GroupSelector__group-opt"}
												style={{
													paddingLeft: `${25 * (group.depth || 0)}px`,
												}}
												ref={checked ? selectedGroupRef : null}
												key={`GroupSelector_${modalClassName}_opt_${index}`}
											>
												<Checkbox
													id={group?.id}
													checked={checked}
													onChange={handleGroupChange}
												/>{" "}
												<Text>{group.name}</Text>
											</div>
										);
									},
								)}
							</div>
						</>
					)}
					{confirmScreenActive && (
						<>
							{confirmContent ? (
								confirmContent(currentGroupSelection!)
							) : (
								<Text tag={"h2"}>
									{strings.GROUP_SELECTOR_CONFIRM.with({
										groupName: formatGroupIdName(currentGroupSelection),
									})}
								</Text>
							)}
							<div className={"GroupSelector__confirm-ctas"}>
								<Button outlined={true} onClick={handleConfirmCancel}>
									{strings.MISC_CANCEL}
								</Button>
								<Button onClick={handleConfirm}>{strings.MISC_CONFIRM}</Button>
							</div>
						</>
					)}
				</div>

				{!confirmScreenActive && (
					<Button
						onClick={scrollToTop}
						className={clsx(
							"GroupSelector__modal-scroll-btn GroupSelector__modal-scroll-top",
							{
								"-visible": scrollToTopActive,
							},
						)}
					>
						<FaArrowUp />
					</Button>
				)}
			</Modal>
		</TrackingContext.Provider>
	);
};

export default React.memo(GroupSelector);
